import React from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import Skeleton from "@mui/material/Skeleton";

import { CloseButton } from "./Buttons";
import RecordActionsKebab, { type RecordActionsKebabProps } from "./RecordActionsKebab";
import { useCurrentUser } from "../hooks/useCurrentUser";
const customer = process.env.REACT_APP_CUSTOMER;

/** Module name segment — Roboto-Light @ 24px (Dez/Alex). Upstream c744112a5 / d795c38fd. */
export const breadcrumbModuleSx: SxProps<Theme> = {
  fontFamily: '"Roboto-Light", "Roboto", sans-serif',
  fontWeight: 300,
  fontSize: "24px",
};

/** Entity / current-page segment — same Roboto-Light @ 24px as the module name. */
export const breadcrumbEntitySx: SxProps<Theme> = {
  fontFamily: '"Roboto-Light", "Roboto", sans-serif',
  fontWeight: 300,
  fontSize: "24px",
};

/** Slash separator between module and entity. */
export const breadcrumbSeparatorSx: SxProps<Theme> = {
  fontFamily: '"Roboto-Light", "Roboto", sans-serif',
  fontWeight: 300,
  fontSize: "24px",
  px: "0.35em",
};

// Hook to get version from build-info.json
const useVersion = () => {
  const [version, setVersion] = React.useState<string>('');
  
  React.useEffect(() => {
    fetch('/app/build-info.json')
      .then(r => r.json())
      .then(info => {
        const versionToUse = customer === "mymd" ? info["mymd-version"] : info.version;
        setVersion(versionToUse);
      })
      .catch(() => setVersion('0.0.0')); // fallback
  }, []);
  
  return version;
};

interface TitleProps {
  className?: string;
  sx?: SxProps<Theme>;
  /** When set, renders `Module / children` with Light faces (upstream breadcrumb, R55-Q3). */
  moduleName?: React.ReactNode;
}

/**
 * Shared page / breadcrumb title. Default size is 24px so list + detail
 * headers stay consistent. Pass `moduleName` on entity detail screens.
 */
export const Title: React.FC<React.PropsWithChildren<TitleProps>> = (props) => {
  const { className, sx = [], moduleName } = props;

  let content: React.ReactNode;
  if (!props.children && !moduleName) {
    content = (
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ minWidth: "160px", maxWidth: "400px", mt: 1.5 }}
        data-testid="title-loading"
      />
    );
  } else if (moduleName) {
    const entity =
      props.children != null && props.children !== false && props.children !== "" ? (
        <Box component="span" sx={breadcrumbEntitySx}>
          {props.children}
        </Box>
      ) : (
        <Skeleton
          animation="wave"
          variant="text"
          sx={{
            display: "inline-block",
            minWidth: "120px",
            maxWidth: "280px",
            verticalAlign: "baseline",
          }}
          data-testid="title-loading"
        />
      );
    content = (
      <>
        <Box component="span" sx={breadcrumbModuleSx}>
          {moduleName}
        </Box>
        <Box component="span" sx={breadcrumbSeparatorSx}>
          /
        </Box>
        {entity}
      </>
    );
  } else {
    content = props.children;
  }

  return (
    <Typography
      variant="h2"
      component="h1"
      className={className}
      sx={[{ fontSize: "24px" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {content}
    </Typography>
  );
};

interface HeaderActionsProps {
  actions?: JSX.Element[];
  closeUrl?: string;
  /** Overrides `closeUrl`-based navigation — e.g. closing a Sheet the header
   *  is embedded in rather than navigating. Passed straight to CloseButton,
   *  which already prefers `onClose` over `closeUrl` when both are given. */
  onClose?: () => void;
  recordActions?: RecordActionsKebabProps;
}

const HeaderActions: React.FC<HeaderActionsProps> = (props) => {
  if (props.actions || props.recordActions) {
    return (
      <Box component="span"  paddingTop={"22px"} flexShrink={0}>
        {/* BP-09: the record's ⋮ leads the header group, left of CLOSE (H233). */}
        {props.recordActions ? <RecordActionsKebab {...props.recordActions} /> : null}
        <CloseButton closeUrl={props.closeUrl} onClose={props.onClose} />
        {(props.actions ?? []).map((action, index) => action)}
      </Box>
    );
  }
  return <></>;
};

export interface HeaderProps {
  /** @figma Pattern 130: centred content in a 76px module-list band.
   * https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=3-1366 */
  presentation?: "moduleList";
  actions?: JSX.Element[];
  children?: React.ReactNode;
  closeUrl?: string;
  onClose?: () => void;
  titleSlot?: React.ReactNode;
  /** Module label shown before the entity title (e.g. Address Book, Quotes). */
  moduleName?: React.ReactNode;
  /**
   * The record's own actions — print its forms and reports, export it (BP-09). The
   * header renders them as ONE ⋮ at the left of the CLOSE / SAVE / SAVE & CLOSE group,
   * in the header buttons' chrome (Aaron 2026-09-30, H233). The module supplies the
   * sections only; placement and colours are the header's.
   */
  recordActions?: RecordActionsKebabProps;
  /**
   * Pin the header — title, ⋮, CLOSE, SAVE / SAVE & CLOSE — to the top of the window
   * while a long single-body record scrolls under it (a Log Book entry's four
   * sections, Aircraft Patch 5). The page scrolls the window and the nav bar is not
   * fixed, so top 0 is the viewport's top. Off by default: every other record is unchanged.
   */
  sticky?: boolean;
}

const Header: React.FC<HeaderProps> = (props) => {
  return (
    <Box
      component="header"
      pl={{ xs: 2, sm: 6 }}
      pr={{ xs: 2, sm: 6 }}
      sx={{
        backgroundColor: grey[200],
        ...(props.sticky ? { position: "sticky", top: 0, zIndex: (theme) => theme.zIndex.appBar } : {}),
      }}
      display="flex"
      data-sticky={props.sticky ? "true" : undefined}
    >
      <Box pt={{ xs: 1, sm: 4 }} pb={{ xs: 1, sm: 2 }} flexGrow={1}>
        <Title moduleName={props.moduleName}>{props.children}</Title>
      </Box>
      <HeaderActions
        actions={props.actions}
        closeUrl={props.closeUrl}
        onClose={props.onClose}
        recordActions={props.recordActions}
      />
    </Box>
  );
};

export const ListingHeader: React.FC<HeaderProps> = (props) => {
  const currentUser = useCurrentUser();
  const version = useVersion();

  // LOCAL ADAPTATION (not in prod): chromeless embed mode (?embed=1) suppresses
  // the grey heading panel so the page renders bare inside an iframe. Relied on
  // by src/onboarding/OnboardingList.tsx, which embeds actionregister and
  // reports-and-forms. Added in d519b485; do not drop on the next GitLab sync.
  if (new URLSearchParams(window.location.search).get("embed") === "1") return null;

  return (
    <Box
      component="header"
      pl={{ xs: 2, sm: 6 }}
      pr={{ xs: 2, sm: 6 }}
      sx={{ backgroundColor: grey[200], ...(props.presentation === "moduleList" ? { minHeight: { sm: 76 }, alignItems: "center", "& > .MuiBox-root": { py: { sm: 0 } } } : {}) }}
      display="flex"
    >
      <Box
        pt={{ xs: 1, sm: 4 }}
        pb={{ xs: 1, sm: 2 }}
        flexGrow={1}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Title>{props.children}</Title>
        {props.titleSlot}
      </Box>
      <Box pt={{ xs: 1, sm: 4 }} pb={{ xs: 1, sm: 2 }} >
      <Typography  sx={{
        fontSize:"14px",
        fontWeight:"500",
        color:"black"
      }}>
      {currentUser ? (
        <>{currentUser.name} - <span style={{fontWeight:"300"}}>{currentUser?.company?.name}</span>&nbsp;&nbsp;&nbsp; |&nbsp;&nbsp;&nbsp; V {version}
        </>
      ) : (
        <Skeleton
          animation="wave"
          variant="text"
          sx={{ minWidth: "160px", maxWidth: "400px", mt: 1.5 }}
          data-testid="title-loading"
        />
      )}
    </Typography>
      </Box>
  
    </Box>
  );
};

export const BreadcrumbHeader = (props) => {
  return (
    <Box
      component="header"
      paddingLeft={{ xs: 2, sm: 6 }}
      paddingRight={{ xs: 2, sm: 6 }}
      bgcolor={grey[200]}
      display="flex"
    >
      <Box
        flexGrow={1}
        paddingTop={{ xs: 2, sm: 3 }}
        paddingBottom={{ sm: 1.5 }}
      >
        {props.children}
      </Box>
      <HeaderActions actions={props.actions} />
    </Box>
  );
};

export const BreadcrumbHeaderListing = (props) => {
  const currentUser = useCurrentUser();
  const version = useVersion();
  return (
    <Box
      component="header"
      paddingLeft={{ xs: 2, sm: 6 }}
      paddingRight={{ xs: 2, sm: 6 }}
      bgcolor={grey[200]}
      display="flex"
    >
      <Box
        flexGrow={1}
        paddingTop={{ xs: 2, sm: 3 }}
        paddingBottom={{ sm: 1.5 }}
      >
        {props.children}
      </Box>
      <Box pt={{ xs: 1, sm: 4 }} pb={{ xs: 1, sm: 2 }} >
          <Typography  sx={{
        fontSize:"14px",
        fontWeight:"500",
        color:"black"
      }}>
      {currentUser ? (
         <>{currentUser.name} - <span style={{fontWeight:"300"}}>{currentUser?.company?.name}</span>&nbsp;&nbsp;&nbsp; |&nbsp;&nbsp;&nbsp; V {version}
        </>
      ) : (
        <Skeleton
          animation="wave"
          variant="text"
          sx={{ minWidth: "160px", maxWidth: "400px", mt: 1.5 }}
          data-testid="title-loading"
        />
      )}
      </Typography>
        </Box>
    </Box>
  );
};

export default Header;
